/**
 * CheckInHistoryTab Component
 * Shows check-in history across all user's OKRs with filters
 */

import React, { useState } from 'react';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { useUrlState } from '../../hooks/useUrlState';
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Star,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
  Building2
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { Card, Badge, Button, LoadingSpinner, EmptyState, Avatar } from '../../components/common';
import { useCheckInHistory, useOrgCheckInHistory } from '../../hooks';
import type { CheckInWithOKR, CheckInStatus, CheckInHistoryFilters } from '../../types/okr';

interface CheckInHistoryTabProps {
  isAccountAdmin?: boolean;
}

const statusConfig: Record<CheckInStatus, { icon: typeof CheckCircle; color: string; bgColor: string; label: string }> = {
  on_track: { icon: CheckCircle, color: 'text-success-600', bgColor: 'bg-success-50', label: 'On Track' },
  at_risk: { icon: AlertTriangle, color: 'text-warning-600', bgColor: 'bg-warning-50', label: 'At Risk' },
  off_track: { icon: AlertCircle, color: 'text-danger-600', bgColor: 'bg-danger-50', label: 'Off Track' },
};

interface CheckInCardProps {
  checkIn: CheckInWithOKR;
  showOwner?: boolean;
}

const CheckInCard: React.FC<CheckInCardProps> = ({ checkIn, showOwner = false }) => {
  const openWithReturn = useOpenWithReturn();
  const [isExpanded, setIsExpanded] = useState(false);
  const statusInfo = statusConfig[checkIn.status];
  const StatusIcon = statusInfo.icon;

  const hasDetails = checkIn.accomplishments || checkIn.challenges || checkIn.nextSteps;

  return (
    <Card className="p-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {/* Status icon */}
          <div className={`p-2 rounded-lg ${statusInfo.bgColor}`}>
            <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
          </div>

          <div>
            {/* OKR Title */}
            <button
              onClick={() => openWithReturn(`/okrs/${checkIn.okrId}`)}
              className="font-medium text-stone-900 hover:text-primary-600 text-left"
            >
              {checkIn.okr.title}
            </button>

            {/* Owner (for org view) */}
            {showOwner && checkIn.user && (
              <div className="flex items-center gap-2 mt-1">
                <Avatar
                  size="sm"
                  src={checkIn.user.avatar || undefined}
                  fallbackText={`${checkIn.user.firstName} ${checkIn.user.lastName}`}
                />
                <span className="text-xs text-stone-500">
                  {checkIn.user.firstName} {checkIn.user.lastName}
                </span>
              </div>
            )}

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-stone-500">
              {/* Date */}
              <span title={format(new Date(checkIn.checkInDate), 'PPpp')}>
                {formatDistanceToNow(new Date(checkIn.checkInDate), { addSuffix: true })}
              </span>

              {/* Status badge */}
              <Badge
                variant={checkIn.status === 'on_track' ? 'success' : checkIn.status === 'at_risk' ? 'warning' : 'danger'}
                size="sm"
              >
                {statusInfo.label}
              </Badge>

              {/* Confidence */}
              {checkIn.confidenceLevel && (
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 text-warning-500 fill-warning-500" />
                  {checkIn.confidenceLevel}/5
                </span>
              )}

              {/* Progress */}
              {checkIn.progressPercentage !== null && checkIn.progressPercentage !== undefined && (
                <span>{checkIn.progressPercentage}% progress</span>
              )}

              {/* Overdue flag */}
              {checkIn.isOverdue && (
                <Badge variant="danger" size="sm">Overdue</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Expand button */}
        {hasDetails && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-stone-400"
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Expanded content */}
      {isExpanded && hasDetails && (
        <div className="mt-4 pt-4 border-t border-stone-100 space-y-3 text-sm">
          {checkIn.accomplishments && (
            <div>
              <h5 className="font-medium text-stone-700 mb-1">Accomplishments</h5>
              <p className="text-stone-600 whitespace-pre-wrap">{checkIn.accomplishments}</p>
            </div>
          )}
          {checkIn.challenges && (
            <div>
              <h5 className="font-medium text-stone-700 mb-1">Challenges</h5>
              <p className="text-stone-600 whitespace-pre-wrap">{checkIn.challenges}</p>
            </div>
          )}
          {checkIn.nextSteps && (
            <div>
              <h5 className="font-medium text-stone-700 mb-1">Next Steps</h5>
              <p className="text-stone-600 whitespace-pre-wrap">{checkIn.nextSteps}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export const CheckInHistoryTab: React.FC<CheckInHistoryTabProps> = ({ isAccountAdmin = false }) => {
  // Status, page and My/Org live in the URL, so opening an OKR and coming Back keeps them.
  const [view, setView] = useUrlState(
    { hstatus: '', hpage: '1', hview: 'my' },
    { hstatus: ['', ...Object.keys(statusConfig)], hview: ['my', 'org'] }
  );
  const filters: CheckInHistoryFilters = {
    page: Math.max(1, Number.parseInt(view.hpage, 10) || 1),
    limit: 20,
  };
  const [showFilters, setShowFilters] = useState(false);
  const statusFilter = view.hstatus as CheckInStatus | '';
  const viewMode = view.hview as 'my' | 'org';
  const setViewMode = (mode: 'my' | 'org') => setView({ hview: mode, hpage: '1' });

  // Fetch my history
  const { data: myData, isLoading: myLoading, error: myError, refetch: myRefetch } = useCheckInHistory({
    ...filters,
    status: statusFilter || undefined
  });

  // Fetch org history (only for account admins)
  const { data: orgData, isLoading: orgLoading, error: orgError, refetch: orgRefetch } = useOrgCheckInHistory({
    ...filters,
    status: statusFilter || undefined
  });

  // Use appropriate data based on view mode
  const data = viewMode === 'org' && isAccountAdmin ? orgData : myData;
  const isLoading = viewMode === 'org' && isAccountAdmin ? orgLoading : myLoading;
  const error = viewMode === 'org' && isAccountAdmin ? orgError : myError;
  const refetch = viewMode === 'org' && isAccountAdmin ? orgRefetch : myRefetch;

  const handleStatusFilterChange = (status: CheckInStatus | '') => {
    setView({ hstatus: status, hpage: '1' });
  };

  const handlePageChange = (page: number) => {
    setView({ hpage: String(page) });
  };

  const clearFilters = () => {
    setView({ hstatus: '', hpage: '1' });
  };

  const hasActiveFilters = !!statusFilter;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Loading check-in history..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center text-danger-600">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          <p>Failed to load check-in history. Please try again.</p>
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* View Toggle for Account Admins */}
      {isAccountAdmin && (
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setViewMode('my')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'my'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            My History
          </button>
          <button
            onClick={() => setViewMode('org')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'org'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            <Building2 className="h-4 w-4" />
            Organization History
          </button>
        </div>
      )}

      {/* Filter Bar */}
      <Card className="p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant={showFilters ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 w-5 h-5 rounded-full bg-primary-600 text-white text-xs flex items-center justify-center">
                  1
                </span>
              )}
            </Button>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {data && (
            <span className="text-sm text-stone-500">
              Showing {data.checkIns.length} of {data.total} check-ins
            </span>
          )}
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-stone-200">
            <div className="flex flex-wrap gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value as CheckInStatus | '')}
                  className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">All statuses</option>
                  <option value="on_track">On Track</option>
                  <option value="at_risk">At Risk</option>
                  <option value="off_track">Off Track</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Check-in List */}
      {data?.checkIns.length === 0 ? (
        <EmptyState
          icon={CheckCircle}
          title="No check-ins yet"
          description={hasActiveFilters
            ? "No check-ins match your filters. Try adjusting your criteria."
            : "You haven't submitted any check-ins yet. Start by checking in on your OKRs!"}
          action={hasActiveFilters ? {
            label: 'Clear Filters',
            onClick: clearFilters
          } : undefined}
        />
      ) : (
        <>
          <div className="space-y-3">
            {data?.checkIns.map((checkIn) => (
              <CheckInCard key={checkIn.id} checkIn={checkIn} showOwner={viewMode === 'org'} />
            ))}
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(filters.page! - 1)}
                disabled={filters.page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-stone-600">
                Page {filters.page} of {data.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(filters.page! + 1)}
                disabled={filters.page === data.totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
